import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPaivat, fetchKattaukset, createVaraus, fetchKapasiteettiForKattaus } from "../supabaseClient";

function Book() {
  const [paivat, setPaivat] = useState([]);
  const [kattaukset, setKattaukset] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // New state: step (1..4)
  const [step, setStep] = useState(1);

  // New state for the 2-step reservation flow
  const [selectedDayId, setSelectedDayId] = useState(null); // stored as string
  const [selectedKattausId, setSelectedKattausId] = useState(null); // stored as string
  const [confirmed, setConfirmed] = useState(false);

  // Reservation form state
  const [form, setForm] = useState({
    name: "",
    people: 0,
    phone: "",
    email: "",
    confirmEmail: "",
    info: "", // Lisätieto (optional)
  });
  // formErrors removed — we no longer show per-field errors in the UI
  // keep peopleChosen for compatibility (updated as we progress)
  const [peopleChosen, setPeopleChosen] = useState(false);

  // boing state for kattauks button animation (moved here so hooks are unconditional)
  const [boingId, setBoingId] = useState(null);
  // control entrance animation for the sittings list when a day is selected
  const [animateSittings, setAnimateSittings] = useState(false);

  // Prevent multiple submits and implement 5s cooldown
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Responsive: track narrow viewport (<500px)
  const [isNarrow, setIsNarrow] = useState(typeof window !== "undefined" ? window.innerWidth < 500 : false);
  useEffect(() => {
    function onResize() {
      setIsNarrow(typeof window !== "undefined" ? window.innerWidth < 500 : false);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // add navigate hook (unconditional, placed with other hooks/state)
  const navigate = useNavigate();

  // mounted ref to avoid setting state on unmounted component (used for cooldown timer)
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // card ref to compute animation start position
  const cardRef = useRef(null);

  // Animation state for levitating number
  const [animatingNumber, setAnimatingNumber] = useState(null); // number being animated or null
  const [animStyle, setAnimStyle] = useState({ left: 0, top: 0, fontSize: 28 });
  const animDurationMs = 1200; // 1.2s animation

  // compute selected objects early so validators and handlers can use capacity
  const selectedDayObj = paivat.find((p) => String(p.id) === String(selectedDayId));
  const selectedKattausObj = kattaukset.find((k) => String(k.id) === String(selectedKattausId));

  // maximum people allowed considering sitting capacity and global cap 7
  const maxPeopleAllowed = Math.min(7, Number(selectedKattausObj?.kapasiteetti ?? 7));

  // New: pure validator that does NOT call setFormErrors (safe to run during render)
  function validateValues(values) {
    const errs = {};
    if (!values.name || values.name.trim().length === 0) {
      errs.name = true;
    }
    const peopleNum = Number(values.people);
    if (!Number.isInteger(peopleNum) || peopleNum < 1 || peopleNum > maxPeopleAllowed) {
      errs.people = true;
    }
    if (!values.phone || values.phone.trim().length === 0) {
      errs.phone = true;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!values.email || !emailRe.test(values.email)) {
      errs.email = true;
    }
    if (!values.confirmEmail || values.confirmEmail !== values.email) {
      errs.confirmEmail = true;
    }
    return Object.keys(errs).length === 0;
  }

  // Call useMemo here (unconditional) to avoid hooks-after-return rule
  const isFormValid = useMemo(() => validateValues(form), [form, maxPeopleAllowed]);

  // Validate and update formErrors (used on blur / submit)
  function validateForm(values) {
    const errs = {};
    if (!values.name || values.name.trim().length === 0) {
      errs.name = "Name is required";
    }
    const peopleNum = Number(values.people);
    if (!Number.isInteger(peopleNum) || peopleNum < 1 || peopleNum > maxPeopleAllowed) {
      errs.people = `Number of people must be between 1 and ${maxPeopleAllowed}`;
    }
    if (!values.phone || values.phone.trim().length === 0) {
      errs.phone = "Phone number is required";
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!values.email || !emailRe.test(values.email)) {
      errs.email = "Valid email is required";
    }
    if (!values.confirmEmail || values.confirmEmail !== values.email) {
      errs.confirmEmail = "Email addresses must match";
    }
    return Object.keys(errs).length === 0;
  }

  // Handle input changes for the form. Special-case "people" so it's numeric and clamped.
  function handleInputChange(e) {
    const { name, value } = e.target;

    if (name === "people") {
      if (value === "") {
        setForm((prev) => ({ ...prev, people: "" }));
      } else {
        let num = Number(value);
        if (!Number.isFinite(num)) num = 0;
        if (num > maxPeopleAllowed) num = maxPeopleAllowed;
        if (num < 0) num = 0;
        setForm((prev) => ({ ...prev, people: num }));
      }
      // no formErrors to clear
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    // no formErrors to clear
  }

  useEffect(() => {
    let mounted = true;
    async function loadData() {
      try {
        setLoading(true);
        const [pResp, kResp] = await Promise.all([fetchPaivat(), fetchKattaukset()]);

        // Support functions that return { data, error } or plain arrays
        const pData = pResp?.data ?? pResp ?? [];
        const kData = kResp?.data ?? kResp ?? [];

        if (!mounted) return;
        setPaivat(Array.isArray(pData) ? pData : []);
        setKattaukset(Array.isArray(kData) ? kData : []);
      } catch (err) {
        if (!mounted) return;
        setError(err.message || String(err));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    // Reset contact fields and confirmation when changing selections/reseting day
    // Preserve chosen number of people so the user doesn't have to reselect it each time
    setForm((prev) => ({
      ...prev,
      name: "",
      phone: "",
      email: "",
      confirmEmail: "",
      info: "",
    }));
    setConfirmed(false);
  }, [selectedDayId, selectedKattausId]);

  // Back navigation between steps (clears deeper selections)
  function goBack() {
    if (step <= 1) return;
    if (step === 2) {
      // from day view back to people
      setStep(1);
      setSelectedDayId(null);
      setSelectedKattausId(null);
      setConfirmed(false);
    } else if (step === 4) {
      // from form back to day + sittings view
      setStep(2);
      setConfirmed(false);
    }
  }

  // New handler: animate chosen number levitating and then advance
  function handleChoosePeopleClick(e, n) {
    // If already animating, ignore repeated clicks
    if (animatingNumber) return;

    const btn = e.currentTarget;
    const cardEl = cardRef.current;
    const animSize = Math.max(28, Math.min(56, 12 + n * 6)); // simple dynamic sizing

    if (btn && cardEl) {
      const btnRect = btn.getBoundingClientRect();
      const cardRect = cardEl.getBoundingClientRect();

      // Compute start position relative to card
      const left = btnRect.left - cardRect.left + btnRect.width / 2 - animSize / 2;
      const top = btnRect.top - cardRect.top + btnRect.height / 2 - animSize / 2;

      setAnimStyle({ left: `${Math.round(left)}px`, top: `${Math.round(top)}px`, fontSize: animSize });
    } else {
      // fallback center of card
      setAnimStyle({ left: "50%", top: "50%", fontSize: animSize, transform: "translate(-50%,-50%)" });
    }

    setAnimatingNumber(n);

    // After the animation ends, commit the choice and move to next step
    setTimeout(() => {
      // If component unmounted before timeout, avoid state updates
      if (!mountedRef.current) return;

      setAnimatingNumber(null);
      setForm((prev) => ({ ...prev, people: n }));
      setPeopleChosen(true);
      setSelectedDayId(null);
      setSelectedKattausId(null);
      setConfirmed(false);
      setStep(2);
    }, animDurationMs);
  }

  // Handlers that advance the step
  function choosePeople(n) {
    setForm((prev) => ({ ...prev, people: n }));
    setPeopleChosen(true);
    setSelectedDayId(null);
    setSelectedKattausId(null);
    setConfirmed(false);
    // hide sittings when returning to people selection
    setAnimateSittings(false);
    setStep(2);
  }

  function chooseDay(idStr) {
    // select a day and stay on step 2, showing sittings underneath
    setSelectedDayId(idStr);
    setSelectedKattausId(null);
    setConfirmed(false);
    setStep(2);
    // trigger entrance animation for the sittings after DOM updates
    setAnimateSittings(false);
    setTimeout(() => {
      if (mountedRef.current) setAnimateSittings(true);
    }, 30);
  }

  function chooseKattaus(kId, cap) {
    // clamp current people to this sitting's capacity
    setSelectedKattausId(kId);
    setForm((prev) => ({ ...prev, people: Math.min(prev.people || 0, cap) }));
    setConfirmed(false);
    // advance to confirmation
    setStep(4);
  }

  // Replace the above handleConfirm with an async version that calls createVaraus
  async function handleConfirm() {
    // Prevent re-entry while submitting
    if (isSubmitting) return;

    // ensure selections
    if (!selectedDayId || !selectedKattausId) {
      return;
    }
    // validate form
    const ok = validateForm(form);
    if (!ok) return;

    setIsSubmitting(true);
    try {
      // sanitize name before sending
      const safeName =
        typeof form.name === "string" ? form.name.trim().slice(0, 200) : String(form.name || "").trim().slice(0, 200);
      // sanitize info (optional) and limit length
      const safeInfo = typeof form.info === "string" ? form.info.trim().slice(0, 1000) : null;

      const payload = {
        paiva: Number(selectedDayId),
        kattaus: Number(selectedKattausId),
        ihmiset: Number(form.people) || null,
        varaaja: safeName || null,
        puhelin: form.phone || null,
        sposti: form.email || null,
        info: safeInfo,
      };

      console.debug("Creating varaus with payload:", payload);

      // call server helper to insert varaus
      const inserted = await createVaraus(payload);

      console.log("Varaus created:", inserted);
      setConfirmed(true);

      // navigate to confirmation page carrying the inserted reservation
      navigate("/confirmation", { state: { reservation: inserted } });
      return;
    } catch (err) {
      console.error("Failed to create varaus:", err);
      setConfirmed(false);
    } finally {
      // keep the button disabled for 5s to avoid duplicate drafts/requests if user stays on page
      setTimeout(() => {
        if (mountedRef.current) {
          setIsSubmitting(false);
        }
      }, 5000);
    }
  }

  if (loading) return <div>Ladataan…</div>;
  if (error) return <div>Virhe: {error}</div>;

  // Ensure consistent string comparison (paiva fk can be number or string)
  // Filter then sort by `aika`. Use localeCompare to handle common time formats like "18:00".
  const filteredKattaukset = selectedDayId
    ? kattaukset
        .filter((k) => String(k.paiva) === String(selectedDayId))
        .filter((k) => {
          const kapasiteetti = k.kapasiteetti ?? 0;
          return kapasiteetti >= (Number(form.people) || 0);
        })
        .slice() // copy before sorting
        .sort((a, b) => {
          const A = a?.aika ?? "";
          const B = b?.aika ?? "";
          return A.localeCompare(B, undefined, { numeric: true, sensitivity: "base" });
        })
    : [];

  const pageStyle = {
    // No 100vh here: this div is a flex child of the app shell, which is
    // already 100dvh tall. A nested 100vh adds the header and the shell's
    // bottom padding on top of a full viewport, so the page always overflows.
    // flex:1 0 auto fills the space that is actually left, and never shrinks
    // the card below its own content.
    flex: "1 0 auto",
    display: "flex",
    justifyContent: "center",
    padding: 0,
    boxSizing: "border-box",
    // inherit global font (Comic Relief via App.js)
    fontFamily: "inherit",
    fontSize: isNarrow ? "0.9em" : "1.2em",
    color: "#000",
  };

  const cardStyle = {
    width: "30vw",
    maxWidth: 560,
    minWidth: 360,
    // fixed height
    height: "78vh",
    minHeight: 650,
    // use the screenshot image from public/ as the card background
    // URL-encode spaces to be safe in CSS urls
    // backgroundImage: "url('./Screenshot%20from%202025-11-23%2017-09-30.png')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    borderRadius: 20,
    border: "0px solid #000", // black 2px border around the page/card area
    padding: 0,
    transform: "perspective(800px) rotateX(0.5deg)",
    // center content
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    overflow: "auto",
    position: "relative",
  };

  const cardInnerStyle = {
    flex: "1 1 auto",
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
  };

  // Small step label
  const stepLabels = {
    1: "1 — Ihmisiä",
    2: "2 — Päivä & Kattaus",
    4: "3 — Vahvista",
  };

  // play boing animation on the clicked sitting button, then select the sitting
  function handleBoingAndChoose(e, kId, cap) {
    if (animatingNumber || boingId) return;
    const duration = 420; // should match CSS animation duration
    setBoingId(kId);
    // after boing, commit selection
    setTimeout(() => {
      if (!mountedRef.current) return;
      setBoingId(null);
      chooseKattaus(kId, cap);
    }, duration);
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle} role="region" aria-label="Booking card" ref={cardRef}>
        {/* Animation CSS inserted locally so no external stylesheet needed */}
        <style>
          {`
            .levitate-number {
              position: absolute;
              pointer-events: none;
              font-weight: 700;
              color: gold;
              text-shadow: 0 6px 14px rgba(0,0,0,0.25);
              transform-origin: center center;
              will-change: transform, opacity;
            }

            @keyframes levitate {
              0% {
                transform: translateY(0) scale(1);
                opacity: 1;
                color: gold;
                filter: drop-shadow(0 6px 8px rgba(0,0,0,0.15));
              }
              60% {
                transform: translateY(-110px) scale(1.5);
                opacity: 0.95;
              }
              100% {
                transform: translateY(-260px) scale(2.2);
                opacity: 0;
                filter: blur(1px) drop-shadow(0 12px 18px rgba(0,0,0,0.2));
              }
            }
           /* Sittings entrance animation: fade + slight lift; per-item stagger via transitionDelay */
           .sittings-item {
             opacity: 0;
             transform: translateY(10px);
             transition: opacity 360ms ease, transform 360ms ease;
           }
           .sittings-item.show {
             opacity: 1;
             transform: translateY(0);
           }
           /* Boing animation for kattauks button */
           @keyframes boing {
             0% { transform: translateY(0) scale(1); }
             30% { transform: translateY(-12px) scale(1.06); }
             55% { transform: translateY(0) scale(0.98); }
             75% { transform: translateY(-6px) scale(1.03); }
             100% { transform: translateY(0) scale(1); }
           }
           .boing-animate {
             animation: boing 420ms cubic-bezier(.2,.8,.2,1);
             will-change: transform;
           }
          `}
        </style>

        <div style={cardInnerStyle}>
          <div>
          </div>

          {/* Animated number element that levitates out */}
          {animatingNumber !== null && (
            <div
              className="levitate-number"
              style={{
                left: animStyle.left,
                top: animStyle.top,
                fontSize: animStyle.fontSize,
                transform: animStyle.transform ? animStyle.transform : undefined,
                animation: `levitate ${animDurationMs}ms cubic-bezier(.22,.9,.38,1) forwards`,
                zIndex: 9999,
              }}
              aria-hidden="true"
            >
              {animatingNumber}
            </div>
          )}

          {/* STEP 1 — People */}
          {step === 1 && (
            <section>
              <h2>Vierasmäärä</h2>
              <div>
                {[1, 2, 3, 4, 5, 6].map((n) => {
                  const disabled = n > maxPeopleAllowed || !!animatingNumber;
                  return (
                    <button
                      key={n}
                      onClick={(e) => handleChoosePeopleClick(e, n)}
                      disabled={disabled}
                      aria-pressed={form.people === n}
                      style={{
                        cursor: disabled ? "not-allowed" : "pointer",
                        backgroundColor: form.people === n ? "gold" : undefined,
                      }}
                    >
                      {n}
                    </button>
                  );
                })}
                </div>
                <p>Yli 6 henkilön varaukset sähköpostitse</p>
                <p>restaurant.scherzo@gmail.com</p>
                </section>
                )}

                {step === 2 && (
                <section>
                  <h2>Valitse päivä</h2>
                  {paivat.length === 0 ? (
                  <div></div>
                  ) : (
                  <div>
                  {paivat.map((p) => {
                  const idStr = String(p.id);
                  const active = idStr === String(selectedDayId);
                  const shouldAuto = idStr === "1" && !selectedDayId;
                  return (
                    <button
                    key={p.id}
                    onClick={() => chooseDay(idStr)}
                    onFocus={() => {
                    // trigger default selection for id "1" when the button receives focus
                    if (!selectedDayId && idStr === "1") chooseDay(idStr);
                    }}
                    autoFocus={shouldAuto}
                    aria-pressed={active}
                    style={{
                    backgroundColor: active ? "gold" : undefined,
                    color: active ? "black" : undefined,
                    }}
                    >
                    {p.paiva}
                    </button>
                  );
                  })}
                  </div>
                  )}

                  {/* If a day is selected, show its sittings directly under the day list */}
                {selectedDayId && (
                <>
                  <div style={{ height: 12 }} />
                  {filteredKattaukset.length === 0 ? (
                  <div>Valitsemallesi seuruekoolle ja päivälle ei löytynyt vapaita kattauksia. Voit tiedustella peruutuspaikkoja puhelimitse <i>0442911021</i> tai sähköpostilla <i>restaurant.scherzo@gmail.com</i></div>
                  ) : (
                  // Render kattaukset side-by-side in a flex row instead of bullet list
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      justifyContent: "center",
                      alignItems: "center",
                      maxWidth: "100%",
                      padding: 4,
                    }}
                  >
                    {filteredKattaukset.map((k, idx) => {
                      const kId = String(k.id);
                      const cap = Number(k.kapasiteetti ?? 7);
                      // add per-item staggered entrance when animateSittings === true
                      const itemClass = animateSittings ? "sittings-item show" : "sittings-item";
                      const delay = animateSittings ? `${idx * 60}ms` : "0ms";
                      return (
                        <div key={k.id} style={{ margin: 0 }}>
                          <button
                            onClick={(e) => handleBoingAndChoose(e, kId, cap)}
                            disabled={!!animatingNumber || !!boingId}
                            className={`${boingId === kId ? "boing-animate" : ""} ${itemClass}`}
                            style={{
                              cursor: !!animatingNumber || !!boingId ? "not-allowed" : "pointer",
                              transitionDelay: delay,
                            }}
                            aria-pressed={kId === String(selectedKattausId)}
                          >
                            {k.aika ?? "Unnamed sitting"}
                          </button>
                        </div>
                      );
                     })}
                    </div>
                    )}
                  </>
                  )}
                  </section>
                  )}

                    {step === 4 && (
                    <section>
                      <h2>Vahvista varaus</h2>
                      <div>
                      <h3>{selectedDayObj ? `${selectedDayObj.paiva} kello` : "—"}  {selectedKattausObj ? `${selectedKattausObj.aika},` : "—"} {form.people} henkilöä</h3>
                      </div>

                      <div style={{ marginTop: 12 }}></div>
                      <h3>Varaustiedot</h3>
                      <div style={{ display: "grid", gap: 8, maxWidth: 420 }}>
                      <label style={{ display: "flex", flexDirection: "column" }}>
                      Varaajan nimi
                      <input
                        name="name"
                        value={form.name}
                        onChange={(e) => handleInputChange(e)}
                        onBlur={() => validateForm(form)}
                        placeholder="Nimesi"
                      />
                      </label>

                      <label style={{ display: "flex", flexDirection: "column" }}>
                      Puhelinnumero
                      <input
                        name="phone"
                        value={form.phone}
                        onChange={(e) => handleInputChange(e)}
                        onBlur={() => validateForm(form)}
                        placeholder="040 333 9999"
                      />
                      </label>

                      <label style={{ display: "flex", flexDirection: "column" }}>
                      Sähköposti
                      <input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => handleInputChange(e)}
                        onBlur={() => validateForm(form)}
                        placeholder="name@example.com"
                      />
                      </label>

                      <label style={{ display: "flex", flexDirection: "column" }}>
                      Sähköposti uudestaan
                      <input
                        name="confirmEmail"
                        type="email"
                        value={form.confirmEmail}
                        onChange={(e) => handleInputChange(e)}
                        onBlur={() => validateForm(form)}
                        placeholder="Sähköposti uudestaan"
                      />
                      </label>

                      <label style={{ display: "flex", flexDirection: "column" }}>
                      Lisätietoja
                      <textarea
                        name="info"
                        value={form.info}
                        onChange={(e) => handleInputChange(e)}
                        placeholder="Lisätietoja ravintolalle"
                        rows={3}
                        style={{ resize: "vertical" }}
                      />
                      </label>
                      </div>

                      {/* Terms dialog will be shown when user clicks the main "Vahvista varaus" button below */}

                                  <dialog
                                  id="termsDialog"
                                  aria-labelledby="termsTitle"
                                  style={{
                                    maxWidth: 520,
                                    padding: 18,
                                    zIndex: 10000,
                                  }}
                                  >
                                  <h3 id="termsTitle">Varausehdot</h3>
                                  <div style={{ maxHeight: "50vh", overflow: "auto", textAlign: "left" }}>
                                    <p>Pöytä on käytettävissä 2,5 tuntia varaushetkestä. Ilmoitathan varauksen yhteydessä kaikista erityisruokavalioista sekä mahdollisista allergioista</p>
                                    <p>Yli 6 hengen varaukset sähköpostitse restaurant.scherzo@gmail.com</p>
                                    <p>Tarjoamme ainoastaan viiden ruokalajin menua, jonka hinta on 55€</p>
                                  </div>
                                  <menu style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-start" }}>
                                    <button
                                    type="button"
                                    onClick={() => {
                                      const dlg = document.getElementById("termsDialog");
                                      if (dlg && typeof dlg.close === "function") dlg.close();
                                    }}
                                    >
                                    Sulje
                                    </button>
                                    <button
                                    type="button"
                                    onClick={() => {
                                      const dlg = document.getElementById("termsDialog");
                                      if (dlg && typeof dlg.close === "function") dlg.close();
                                      // proceed with the actual confirmation
                                      // call handleConfirm directly (it handles isSubmitting / navigation)
                                      setTimeout(() => {
                                      typeof handleConfirm === "function" && handleConfirm();
                                      }, 20);
                                    }}
                                    >
                                    Vahvista varaus
                                    </button>
                                  </menu>
                                  </dialog>

                                  <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "center" }}>
                                  <button
                                    onClick={() => {
                            // open terms dialog; if dialog unsupported, use a confirm() fallback
                            const dlg = document.getElementById("termsDialog");
                            if (dlg && typeof dlg.showModal === "function") {
                              dlg.showModal();
                              return;
                            }
                            // fallback: show simple confirm and proceed if accepted
                            const ok = window.confirm(
                              "Varausehdot:\n\nPöytä on käytettävissä 2,5 tuntia varaushetkestä. Hyväksytkö varausehdot?"
                            );
                            if (ok) handleConfirm();
                          }}
                          disabled={!selectedDayId || !selectedKattausId || !isFormValid || isSubmitting}
                          aria-busy={isSubmitting}
                          title={isSubmitting ? "Vahvistetaan..." : "Vahvista varaus"}
                        >
                          {isSubmitting ? "⏳ Vahvistetaan..." : "Vahvista varaus"}
                        </button>

                       <button
                       onClick={() => {
                       // allow user to choose another day, keep people
                       setSelectedDayId(null);
                       setSelectedKattausId(null);
                       setConfirmed(false);
                       setStep(2);
                       }}
                       >
                       Aloita alusta
                       </button>
                       </div>

                       {confirmed && (
                       <div style={{ marginTop: 12, padding: 12, background: "#e6ffe6", border: "1px solid #b2ffb2" }}>
                       Reservation confirmed for {selectedDayObj?.paiva} at {selectedKattausObj?.aika}.
                       <div>
                       {form.name} — {form.people} {form.people === 1 ? "person" : "people"} — {form.phone} — {form.email}
                       </div>
                       </div>
                       )}
                     </section>
                     )}

                    {/* Footer Back button placed in normal flow 20px beneath the previous element */}
          {step > 1 && (
            <div
              style={{
                marginTop: 20, // 20px beneath the preceding element
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <button onClick={goBack}>← Takaisin</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Book;
